
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle2, Clock, Code2, Database, FileCode2, LucideIcon, Network, Server, Shield, TestTube2, Wrench } from 'lucide-react';

// Task type definition
type TodoTask = {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  category: 'infrastructure' | 'api' | 'agents' | 'ui' | 'testing' | 'security';
  priority: 'high' | 'medium' | 'low';
};

// Initial tasks for agentic implementation
const initialTasks: TodoTask[] = [
  // Infrastructure tasks
  {
    id: 'infra-1',
    title: 'Configure SPARC bridge in production',
    description: 'Set up SPARC bridge edge function with proper error handling and API key management',
    completed: true, // Now completed
    category: 'infrastructure',
    priority: 'high',
  },
  {
    id: 'infra-2',
    title: 'Implement proper edge function monitoring',
    description: 'Add logging, monitoring and alerting for edge functions',
    completed: false,
    category: 'infrastructure',
    priority: 'medium',
  },
  
  // API tasks
  {
    id: 'api-1',
    title: 'Replace mock API responses with real calls',
    description: 'Update agent hooks to use actual API connections instead of mock responses',
    completed: true, // Now completed
    category: 'api',
    priority: 'high',
  },
  {
    id: 'api-2',
    title: 'Implement error handling for API calls',
    description: 'Add comprehensive error handling and retries for API requests',
    completed: true, // Now completed
    category: 'api',
    priority: 'high',
  },
  
  // Agent tasks
  {
    id: 'agent-1',
    title: 'Configure proper agent authentication',
    description: 'Implement role-based access control for different agent capabilities',
    completed: false,
    category: 'agents',
    priority: 'high',
  },
  {
    id: 'agent-2',
    title: 'Implement agent session persistence',
    description: 'Store agent sessions and history in the database',
    completed: true, // Now completed
    category: 'agents',
    priority: 'medium',
  },
  {
    id: 'agent-3',
    title: 'Create feedback loop for agent responses',
    description: 'Add rating system for agent responses to improve quality over time',
    completed: false,
    category: 'agents',
    priority: 'low',
  },
  
  // UI tasks
  {
    id: 'ui-1',
    title: 'Improve agent interaction UI',
    description: 'Enhance the agent request form with better feedback and loading states',
    completed: true, // Now completed
    category: 'ui',
    priority: 'medium',
  },
  {
    id: 'ui-2',
    title: 'Implement agent history view',
    description: 'Add a UI to view past agent interactions and results',
    completed: true, // Now completed
    category: 'ui',
    priority: 'medium',
  },
  
  // Testing tasks
  {
    id: 'test-1',
    title: 'Create agent integration tests',
    description: 'Develop automated tests for agent interactions and workflows',
    completed: false,
    category: 'testing',
    priority: 'medium',
  },
  
  // Security tasks
  {
    id: 'sec-1',
    title: 'Implement rate limiting for agent requests',
    description: 'Add rate limiting to prevent abuse of agent capabilities',
    completed: false,
    category: 'security',
    priority: 'high',
  },
  {
    id: 'sec-2',
    title: 'Audit agent permissions and access controls',
    description: 'Review and secure agent access patterns and permissions',
    completed: false,
    category: 'security',
    priority: 'high',
  }
];

// Category icons mapping
const categoryIcons: Record<string, LucideIcon> = {
  'infrastructure': Server,
  'api': Network,
  'agents': Code2,
  'ui': FileCode2,
  'testing': TestTube2,
  'security': Shield,
};

const DevTodoList: React.FC = () => {
  const [tasks, setTasks] = useState<TodoTask[]>(initialTasks);
  const [activeTab, setActiveTab] = useState<string>('all');
  
  // Toggle task completion
  const toggleTaskCompletion = (taskId: string) => {
    setTasks(tasks.map(task => 
      task.id === taskId ? { ...task, completed: !task.completed } : task
    ));
  };
  
  // Filter tasks based on active tab
  const getFilteredTasks = () => {
    if (activeTab === 'all') {
      return tasks;
    } else if (activeTab === 'completed') {
      return tasks.filter(task => task.completed);
    } else if (activeTab === 'pending') {
      return tasks.filter(task => !task.completed);
    } else {
      // Filter by category
      return tasks.filter(task => task.category === activeTab);
    }
  };
  
  // Calculate progress
  const completedTasks = tasks.filter(task => task.completed).length;
  const totalTasks = tasks.length;
  const progressPercentage = Math.round((completedTasks / totalTasks) * 100);
  
  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Render category icon
  const renderCategoryIcon = (category: string) => {
    const IconComponent = categoryIcons[category] || Code2;
    return <IconComponent className="h-4 w-4 mr-1" />;
  };
  
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Development To-Do List</span>
          <Badge variant="outline" className="ml-2">
            {completedTasks} / {totalTasks} ({progressPercentage}%)
          </Badge>
        </CardTitle>
        <CardDescription>
          Track implementation progress of agentic functionality
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 md:grid-cols-7 mb-4">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="pending">Pending</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
            <TabsTrigger value="infrastructure">Infrastructure</TabsTrigger>
            <TabsTrigger value="api">API</TabsTrigger>
            <TabsTrigger value="agents">Agents</TabsTrigger>
            <TabsTrigger value="ui">UI</TabsTrigger>
          </TabsList>
          
          <TabsContent value={activeTab} className="mt-0">
            <div className="space-y-4">
              {getFilteredTasks().map(task => (
                <div key={task.id} className="flex items-start space-x-2 border p-3 rounded-md">
                  <Checkbox 
                    id={task.id} 
                    checked={task.completed}
                    onCheckedChange={() => toggleTaskCompletion(task.id)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <label
                        htmlFor={task.id}
                        className={`font-medium ${task.completed ? 'line-through text-muted-foreground' : ''}`}
                      >
                        {task.title}
                      </label>
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline" className="flex items-center">
                          {renderCategoryIcon(task.category)}
                          {task.category}
                        </Badge>
                        <Badge className={`${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </Badge>
                        {task.completed && (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Done
                          </Badge>
                        )}
                      </div>
                    </div>
                    <p className={`text-sm text-muted-foreground ${task.completed ? 'line-through' : ''}`}>
                      {task.description}
                    </p>
                  </div>
                </div>
              ))}
              
              {getFilteredTasks().length === 0 && (
                <div className="flex items-center justify-center p-8 text-center">
                  <div>
                    <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">No tasks found for this filter</p>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
      
      <CardFooter className="flex justify-between border-t pt-6">
        <div className="text-sm text-muted-foreground">
          {completedTasks} of {totalTasks} tasks completed
        </div>
        <Button variant="outline" size="sm" onClick={() => window.print()} className="hidden md:block">
          Export List
        </Button>
      </CardFooter>
    </Card>
  );
};

export default DevTodoList;
